// buildInfo — which build of the app is running, pure.
//
// Vite stamps `__BILDA_BUILD__` at build time (vite.config.ts): the version
// release-please keeps in the root package.json, plus the commit when one is
// known (CI's BILDA_COMMIT, else the local git HEAD). The value is a
// compile-time constant, parsed once here at the boundary into a sum: a
// build either knows its commit or it does not, never a half-empty record.

import { exhaustSwitchCase } from "@bilda/utils";

export type BuildInfo =
    | {
          readonly type: "stamped";
          readonly version: string;
          readonly commit: string;
      }
    | { readonly type: "unstamped"; readonly version: string };

const str = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;

// Parse the raw stamp. A missing or malformed version degrades to "dev"
// (never a crash, never a fabricated number).
export const parseBuildInfo = (raw: unknown): BuildInfo => {
    const record = new Map<string, unknown>(
        typeof raw === "object" && raw !== null ? Object.entries(raw) : [],
    );
    const version = str(record.get("version")) ?? "dev";
    const commit = str(record.get("commit"));
    return commit === undefined
        ? { type: "unstamped", version }
        : { type: "stamped", version, commit };
};

// The short commit people read and paste (git's default abbreviation).
const shortCommit = (commit: string): string => commit.slice(0, 7);

// "0.5.0 (a1b2c3d)" — the version with its build, as copied for support.
export const versionWithBuild = (b: BuildInfo): string => {
    switch (b.type) {
        case "stamped":
            return `${b.version} (${shortCommit(b.commit)})`;
        case "unstamped":
            return b.version;
        default:
            return exhaustSwitchCase(b);
    }
};

// `typeof` guards a runner that loads this file without the Vite define.
export const buildInfo: BuildInfo = parseBuildInfo(
    typeof __BILDA_BUILD__ === "undefined" ? undefined : __BILDA_BUILD__,
);
